// ============================================================================
// BP-04-02: Documents Frontend - DocumentUploader Component
// ============================================================================

import React, { useState, useRef, useCallback } from 'react';
import {
  Upload,
  X,
  FileText,
  Image,
  Table,
  File,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { cn } from '@/core/lib/utils';
import type { UploadProgress, DocumentType } from '../types/documentsTypes';
import { DOCUMENT_TYPE_LABELS, ALLOWED_FILE_TYPES, MAX_FILE_SIZE_BYTES } from '../types/documentsTypes';
import { formatFileSize, getFilesFromDragEvent } from '../hooks/useDocumentUpload';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface DocumentUploaderProps {
  onFilesSelected: (files: File[]) => void;
  uploadQueue: UploadProgress[];
  onCancelUpload: (filename: string) => void;
  onRemoveFromQueue: (filename: string) => void;
  isUploading: boolean;
  accept?: string;
  maxFiles?: number;
  defaultDocumentType?: DocumentType;
  className?: string;
}

// -----------------------------------------------------------------------------
// Helper Components
// -----------------------------------------------------------------------------

const getFileIcon = (file: File): React.ReactNode => {
  const mimeType = file.type;
  const iconProps = { className: 'w-6 h-6' };

  if (mimeType.startsWith('image/')) {
    return <Image {...iconProps} className="w-6 h-6 text-blue-500" />;
  }
  if (mimeType === 'application/pdf') {
    return <FileText {...iconProps} className="w-6 h-6 text-red-500" />;
  }
  if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || mimeType === 'text/csv') {
    return <Table {...iconProps} className="w-6 h-6 text-green-500" />;
  }
  return <File {...iconProps} className="w-6 h-6 text-warm-gray" />;
};

const getStatusIcon = (status: UploadProgress['status']): React.ReactNode => {
  switch (status) {
    case 'complete':
      return <CheckCircle2 className="w-5 h-5 text-sage" />;
    case 'error':
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    case 'uploading':
    case 'processing':
      return <Loader2 className="w-5 h-5 text-sage animate-spin" />;
    default:
      return null;
  }
};

// -----------------------------------------------------------------------------
// UploadQueueItem Component
// -----------------------------------------------------------------------------

interface UploadQueueItemProps {
  item: UploadProgress;
  onCancel: () => void;
  onRemove: () => void;
}

const UploadQueueItem: React.FC<UploadQueueItemProps> = ({ item, onCancel, onRemove }) => {
  const { file, progress, status, error } = item;

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-lg border transition-all duration-200',
        status === 'error'
          ? 'bg-red-50 border-red-200'
          : status === 'complete'
          ? 'bg-sage/5 border-sage/20'
          : 'bg-white border-warm-gray/10'
      )}
    >
      {/* File icon */}
      <div className="flex-shrink-0">{getFileIcon(file)}</div>

      {/* File info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-navy truncate">{file.name}</p>
        <div className="flex items-center gap-2 text-xs text-warm-gray">
          <span>{formatFileSize(file.size)}</span>
          {status === 'uploading' && <span>• {progress}%</span>}
          {status === 'processing' && <span>• Processing...</span>}
          {status === 'error' && (
            <span className="text-red-500 truncate">• {error}</span>
          )}
        </div>

        {/* Progress bar */}
        {(status === 'uploading' || status === 'processing') && (
          <div className="mt-2 h-1.5 bg-cream-dark rounded-full overflow-hidden">
            <div
              className="h-full bg-sage rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>

      {/* Status icon */}
      <div className="flex-shrink-0">{getStatusIcon(status)}</div>

      {/* Action button */}
      <button
        onClick={status === 'uploading' ? onCancel : onRemove}
        className={cn(
          'flex-shrink-0 p-1 rounded-full transition-colors',
          status === 'uploading'
            ? 'hover:bg-red-100 text-red-500'
            : 'hover:bg-warm-gray/10 text-warm-gray'
        )}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// -----------------------------------------------------------------------------
// DocumentUploader Component
// -----------------------------------------------------------------------------

export const DocumentUploader: React.FC<DocumentUploaderProps> = ({
  onFilesSelected,
  uploadQueue,
  onCancelUpload,
  onRemoveFromQueue,
  isUploading,
  accept,
  maxFiles = 10,
  className,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = getFilesFromDragEvent(e);
      if (files.length > 0) {
        onFilesSelected(files.slice(0, maxFiles));
      }
    },
    [onFilesSelected, maxFiles]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(e.target.files || []);
      if (files.length > 0) {
        onFilesSelected(files.slice(0, maxFiles));
      }
      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [onFilesSelected, maxFiles]
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const acceptedTypes = accept || ALLOWED_FILE_TYPES.join(',');

  return (
    <div className={cn('space-y-4', className)}>
      {/* Drop zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleBrowseClick}
        className={cn(
          'relative flex flex-col items-center justify-center gap-4 p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200',
          isDragOver
            ? 'border-sage bg-sage/5 scale-[1.02]'
            : 'border-warm-gray/20 hover:border-sage-light hover:bg-cream-dark/50'
        )}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes}
          multiple
          onChange={handleFileInputChange}
          className="hidden"
        />

        {/* Icon */}
        <div
          className={cn(
            'p-4 rounded-full transition-colors duration-200',
            isDragOver ? 'bg-sage/10 text-sage' : 'bg-cream text-warm-gray'
          )}
        >
          <Upload className="w-8 h-8" />
        </div>

        {/* Text */}
        <div className="text-center">
          <p className="text-lg font-medium text-navy">
            {isDragOver ? 'Drop files here' : 'Drag & drop files here'}
          </p>
          <p className="text-sm text-warm-gray mt-1">
            or{' '}
            <span className="text-sage font-medium">browse</span>
            {' '}to select
          </p>
        </div>

        {/* Supported formats */}
        <p className="text-xs text-warm-gray">
          Supports: PDF, Images, Word, Excel, Text • Max {formatFileSize(MAX_FILE_SIZE_BYTES)} per file
        </p>
      </div>

      {/* Upload queue */}
      {uploadQueue.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium text-navy">
              {isUploading ? 'Uploading...' : 'Upload Queue'}
            </h4>
            {uploadQueue.some((item) => item.status === 'complete' || item.status === 'error') && (
              <button
                onClick={() => {
                  uploadQueue
                    .filter((item) => item.status === 'complete' || item.status === 'error')
                    .forEach((item) => onRemoveFromQueue(item.file.name));
                }}
                className="text-xs text-sage hover:text-sage-dark transition-colors"
              >
                Clear completed
              </button>
            )}
          </div>

          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {uploadQueue.map((item) => (
              <UploadQueueItem
                key={item.file.name}
                item={item}
                onCancel={() => onCancelUpload(item.file.name)}
                onRemove={() => onRemoveFromQueue(item.file.name)}
              />
            ))}
          </div>

          {/* Summary */}
          {isUploading && (
            <div className="flex items-center justify-between pt-2 border-t border-warm-gray/10">
              <span className="text-sm text-warm-gray">
                {uploadQueue.filter((item) => item.status === 'uploading').length} of{' '}
                {uploadQueue.length} files uploading
              </span>
              <span className="text-sm font-medium text-sage">
                {Math.round(
                  uploadQueue.reduce((sum, item) => sum + item.progress, 0) / uploadQueue.length
                )}
                % complete
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------
// Compact Uploader (for inline use)
// -----------------------------------------------------------------------------

interface CompactUploaderProps {
  onFilesSelected: (files: File[]) => void;
  className?: string;
}

export const CompactUploader: React.FC<CompactUploaderProps> = ({
  onFilesSelected,
  className,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={ALLOWED_FILE_TYPES.join(',')}
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) {
            onFilesSelected(files);
          }
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        }}
        className="hidden"
      />
      <button
        onClick={() => fileInputRef.current?.click()}
        className={cn(
          'flex items-center gap-2 px-4 py-2 bg-sage text-white rounded-lg font-medium hover:bg-sage-dark transition-colors',
          className
        )}
      >
        <Upload className="w-4 h-4" />
        Upload Files
      </button>
    </>
  );
};

export default DocumentUploader;

